import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@clerk/react";
import { useOrg } from "@/lib/orgContext";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Loader2, Plus, Pencil, Trash2, GripVertical, ChevronUp, ChevronDown, X } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

type FieldType = "text" | "long_text" | "number" | "currency" | "date" | "dropdown" | "checkbox";
type EntityType = "contract" | "coach";

type FieldDef = {
  id: number;
  orgId: number;
  entityType: EntityType;
  key: string;
  label: string;
  type: FieldType;
  options: string[];
  required: boolean;
  helpText: string | null;
  displayOrder: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
};

const FIELD_TYPE_LABELS: Record<FieldType, string> = {
  text: "Short Text",
  long_text: "Long Text",
  number: "Number",
  currency: "Currency ($)",
  date: "Date",
  dropdown: "Dropdown",
  checkbox: "Checkbox (Yes/No)",
};

const KEY_REGEX = /^[a-z0-9_]+$/;

type FormState = {
  key: string;
  label: string;
  type: FieldType;
  options: string[];
  required: boolean;
  helpText: string;
  displayOrder: number;
  enabled: boolean;
};

const defaultForm = (): FormState => ({
  key: "",
  label: "",
  type: "text",
  options: [],
  required: false,
  helpText: "",
  displayOrder: 0,
  enabled: true,
});

export function CustomFieldsSettings() {
  const { getToken } = useAuth();
  const { org } = useOrg();
  const { toast } = useToast();
  const isAdmin = org?.role === "admin";

  const [definitions, setDefinitions] = useState<FieldDef[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState<EntityType>("contract");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<FormState>(defaultForm());
  const [newOption, setNewOption] = useState("");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});

  const [deleteTarget, setDeleteTarget] = useState<FieldDef | null>(null);

  const load = useCallback(async () => {
    try {
      const token = await getToken();
      const res = await fetch("/api/custom-fields/definitions", {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = (await res.json()) as FieldDef[];
      setDefinitions(data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  const filtered = definitions
    .filter((d) => d.entityType === activeTab)
    .sort((a, b) => a.displayOrder - b.displayOrder || a.id - b.id);

  function openCreate() {
    setEditingId(null);
    setForm({ ...defaultForm(), displayOrder: filtered.length });
    setFormErrors({});
    setNewOption("");
    setModalOpen(true);
  }

  function openEdit(def: FieldDef) {
    setEditingId(def.id);
    setForm({
      key: def.key,
      label: def.label,
      type: def.type,
      options: def.options ?? [],
      required: def.required,
      helpText: def.helpText ?? "",
      displayOrder: def.displayOrder,
      enabled: def.enabled,
    });
    setFormErrors({});
    setNewOption("");
    setModalOpen(true);
  }

  function validateForm(): boolean {
    const errors: Record<string, string> = {};
    if (!form.label.trim()) errors["label"] = "Label is required";
    if (!editingId) {
      if (!form.key.trim()) {
        errors["key"] = "Key is required";
      } else if (!KEY_REGEX.test(form.key)) {
        errors["key"] = "Key must be lowercase letters, numbers, and underscores only";
      }
    }
    if (form.type === "dropdown" && form.options.length === 0) {
      errors["options"] = "Add at least one option for a dropdown field";
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }

  async function handleSave() {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const token = await getToken();
      const body = editingId
        ? {
            label: form.label,
            options: form.options,
            required: form.required,
            helpText: form.helpText || null,
            displayOrder: form.displayOrder,
            enabled: form.enabled,
          }
        : {
            entityType: activeTab,
            key: form.key,
            label: form.label,
            type: form.type,
            options: form.options,
            required: form.required,
            helpText: form.helpText || null,
            displayOrder: form.displayOrder,
            enabled: form.enabled,
          };

      const url = editingId
        ? `/api/custom-fields/definitions/${editingId}`
        : "/api/custom-fields/definitions";
      const method = editingId ? "PUT" : "POST";

      const res = await fetch(url, {
        method,
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify(body),
      });

      if (!res.ok) {
        const data = (await res.json().catch(() => ({}))) as { error?: string };
        if (res.status === 409) {
          setFormErrors({ key: "A field with this key already exists for this entity type" });
          return;
        }
        toast({ title: "Error", description: data.error ?? "Could not save", variant: "destructive" });
        return;
      }

      const saved = (await res.json()) as FieldDef;
      if (editingId) {
        setDefinitions((prev) => prev.map((d) => (d.id === saved.id ? saved : d)));
      } else {
        setDefinitions((prev) => [...prev, saved]);
      }
      setModalOpen(false);
      toast({ title: editingId ? "Field updated" : "Field created" });
    } catch {
      toast({ title: "Network error", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  }

  async function handleToggleEnabled(def: FieldDef) {
    const token = await getToken();
    const res = await fetch(`/api/custom-fields/definitions/${def.id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ enabled: !def.enabled }),
    });
    if (res.ok) {
      const updated = (await res.json()) as FieldDef;
      setDefinitions((prev) => prev.map((d) => (d.id === updated.id ? updated : d)));
    }
  }

  async function handleMoveOrder(def: FieldDef, direction: "up" | "down") {
    const siblings = filtered;
    const idx = siblings.findIndex((d) => d.id === def.id);
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= siblings.length) return;

    const swapWith = siblings[targetIdx];
    const newOrder = swapWith.displayOrder;
    const swapOrder = def.displayOrder;

    const token = await getToken();
    await Promise.all([
      fetch(`/api/custom-fields/definitions/${def.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ displayOrder: newOrder }),
      }),
      fetch(`/api/custom-fields/definitions/${swapWith.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ displayOrder: swapOrder }),
      }),
    ]);

    setDefinitions((prev) =>
      prev.map((d) => {
        if (d.id === def.id) return { ...d, displayOrder: newOrder };
        if (d.id === swapWith.id) return { ...d, displayOrder: swapOrder };
        return d;
      })
    );
  }

  async function handleDelete() {
    if (!deleteTarget) return;
    const token = await getToken();
    const res = await fetch(`/api/custom-fields/definitions/${deleteTarget.id}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (res.status === 204) {
      // Hard-deleted: field had no values, safe to remove from state
      setDefinitions((prev) => prev.filter((d) => d.id !== deleteTarget.id));
      toast({ title: "Field deleted" });
    } else if (res.ok) {
      // Soft-disabled: field had existing values — update local state to reflect disabled status
      const body = (await res.json()) as { disabled: boolean; definition: FieldDef };
      if (body.disabled) {
        setDefinitions((prev) => prev.map((d) => (d.id === body.definition.id ? body.definition : d)));
        toast({
          title: "Field disabled",
          description: "This field has stored values and has been disabled rather than deleted. You can re-enable it at any time.",
        });
      }
    } else {
      toast({ title: "Could not delete field", variant: "destructive" });
    }
    setDeleteTarget(null);
  }

  function addOption() {
    const opt = newOption.trim();
    if (!opt || form.options.includes(opt)) return;
    setForm((f) => ({ ...f, options: [...f.options, opt] }));
    setNewOption("");
  }

  function removeOption(opt: string) {
    setForm((f) => ({ ...f, options: f.options.filter((o) => o !== opt) }));
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          Custom Fields
        </CardTitle>
        <CardDescription>
          Define org-specific fields that appear on contracts and coach profiles. Values are preserved when a field is disabled.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as EntityType)}>
          <div className="flex items-center justify-between">
            <TabsList>
              <TabsTrigger value="contract">Contract Fields</TabsTrigger>
              <TabsTrigger value="coach">Coach Fields</TabsTrigger>
            </TabsList>
            {isAdmin && (
              <Button size="sm" onClick={openCreate}>
                <Plus className="h-4 w-4 mr-1" /> Add Field
              </Button>
            )}
          </div>

          {(["contract", "coach"] as EntityType[]).map((et) => (
            <TabsContent key={et} value={et} className="space-y-3 mt-4">
              {loading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading fields…
                </div>
              ) : filtered.length === 0 ? (
                <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                  No custom fields defined for {et === "contract" ? "contracts" : "coaches"} yet.
                  {isAdmin && (
                    <Button variant="link" className="block mx-auto mt-1 h-auto p-0 text-sm" onClick={openCreate}>
                      Add the first field
                    </Button>
                  )}
                </div>
              ) : (
                <div className="rounded-md border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                      <tr>
                        <th className="text-left font-medium px-3 py-2">Label</th>
                        <th className="text-left font-medium px-3 py-2">Key</th>
                        <th className="text-left font-medium px-3 py-2">Type</th>
                        <th className="text-left font-medium px-3 py-2">Required</th>
                        <th className="text-left font-medium px-3 py-2">Enabled</th>
                        {isAdmin && <th className="text-right font-medium px-3 py-2">Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((def, idx) => (
                        <tr key={def.id} className="border-t">
                          <td className="px-3 py-2 font-medium">
                            {def.label}
                            {def.helpText && (
                              <span className="ml-1 text-xs text-muted-foreground font-normal">— {def.helpText}</span>
                            )}
                          </td>
                          <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{def.key}</td>
                          <td className="px-3 py-2">
                            <Badge variant="outline" className="text-xs">{FIELD_TYPE_LABELS[def.type]}</Badge>
                          </td>
                          <td className="px-3 py-2">
                            {def.required ? <Badge variant="secondary" className="text-xs">Required</Badge> : <span className="text-muted-foreground text-xs">Optional</span>}
                          </td>
                          <td className="px-3 py-2">
                            {isAdmin ? (
                              <Switch
                                checked={def.enabled}
                                onCheckedChange={() => handleToggleEnabled(def)}
                                className="scale-90"
                              />
                            ) : (
                              <span className={def.enabled ? "text-emerald-600 text-xs" : "text-muted-foreground text-xs"}>
                                {def.enabled ? "On" : "Off"}
                              </span>
                            )}
                          </td>
                          {isAdmin && (
                            <td className="px-3 py-2">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleMoveOrder(def, "up")} disabled={idx === 0} title="Move up">
                                  <ChevronUp className="h-3.5 w-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleMoveOrder(def, "down")} disabled={idx === filtered.length - 1} title="Move down">
                                  <ChevronDown className="h-3.5 w-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(def)}>
                                  <Pencil className="h-3.5 w-3.5" />
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => setDeleteTarget(def)}>
                                  <Trash2 className="h-3.5 w-3.5" />
                                </Button>
                              </div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>

        {/* Create / Edit Modal */}
        <Dialog open={modalOpen} onOpenChange={setModalOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit Custom Field" : "Add Custom Field"}</DialogTitle>
              <DialogDescription>
                {editingId
                  ? "You can update the label, options, and settings. The key is immutable once created."
                  : `Define a new custom field for ${activeTab === "contract" ? "contracts" : "coaches"}.`}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-2">
              {!editingId && (
                <div className="space-y-1.5">
                  <Label htmlFor="cf-key">
                    Internal Key <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="cf-key"
                    value={form.key}
                    onChange={(e) => setForm((f) => ({ ...f, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") }))}
                    placeholder="e.g. cost_center_code"
                  />
                  <p className="text-xs text-muted-foreground">Lowercase letters, numbers, and underscores. Immutable once created.</p>
                  {formErrors["key"] && <p className="text-xs text-destructive">{formErrors["key"]}</p>}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="cf-label">
                  Display Label <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="cf-label"
                  value={form.label}
                  onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                  placeholder="e.g. Cost Center Code"
                />
                {formErrors["label"] && <p className="text-xs text-destructive">{formErrors["label"]}</p>}
              </div>

              {!editingId && (
                <div className="space-y-1.5">
                  <Label htmlFor="cf-type">Field Type</Label>
                  <Select
                    value={form.type}
                    onValueChange={(v) => setForm((f) => ({ ...f, type: v as FieldType, options: [] }))}
                  >
                    <SelectTrigger id="cf-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {(Object.entries(FIELD_TYPE_LABELS) as [FieldType, string][]).map(([k, label]) => (
                        <SelectItem key={k} value={k}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {form.type === "dropdown" && (
                <div className="space-y-1.5">
                  <Label>Dropdown Options</Label>
                  <div className="flex gap-2">
                    <Input
                      value={newOption}
                      onChange={(e) => setNewOption(e.target.value)}
                      placeholder="Add option…"
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOption(); } }}
                    />
                    <Button type="button" variant="outline" onClick={addOption}>Add</Button>
                  </div>
                  {form.options.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mt-2">
                      {form.options.map((opt) => (
                        <span key={opt} className="inline-flex items-center gap-1 text-xs bg-muted px-2 py-1 rounded-full">
                          {opt}
                          <button type="button" onClick={() => removeOption(opt)} className="hover:text-destructive">
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))}
                    </div>
                  )}
                  {formErrors["options"] && <p className="text-xs text-destructive">{formErrors["options"]}</p>}
                </div>
              )}

              <div className="space-y-1.5">
                <Label htmlFor="cf-help">Help Text (optional)</Label>
                <Input
                  id="cf-help"
                  value={form.helpText}
                  onChange={(e) => setForm((f) => ({ ...f, helpText: e.target.value }))}
                  placeholder="Brief description shown below the field"
                />
              </div>

              <div className="flex items-center gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={form.required}
                    onCheckedChange={(c) => setForm((f) => ({ ...f, required: !!c }))}
                  />
                  <span className="text-sm">Required</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <Switch
                    checked={form.enabled}
                    onCheckedChange={(c) => setForm((f) => ({ ...f, enabled: c }))}
                    className="scale-90"
                  />
                  <span className="text-sm">Enabled</span>
                </label>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingId ? "Save Changes" : "Create Field"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete confirm dialog */}
        <AlertDialog open={!!deleteTarget} onOpenChange={(o) => { if (!o) setDeleteTarget(null); }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete custom field?</AlertDialogTitle>
              <AlertDialogDescription>
                The field <strong>{deleteTarget?.label}</strong> and its definition will be removed.
                Stored values are retained in the database but will no longer be visible until the field is re-created with the same key.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </CardContent>
    </Card>
  );
}
